import { MouseEvent, useState } from "react";

import { useNavigate, useParams, useRouterState } from "@tanstack/react-router";

import {
  CASE_ACCESS_COPY,
  CASE_ACCESS_STATE,
  caseAccessStateForStatus,
} from "@constants/case-access";
import { NAVIGATION_PATHS } from "@constants/navigation";

import { useCaseByCaseNumber } from "@hooks/queries/use-cases";
import { useIsAdmin } from "@hooks/queries/use-me";
import { useNavigationWarning } from "@hooks/useNavigationWarning";

import type { ApiError } from "@utils/callApi";

import { buildCaseSubtitle } from "./build-case-subtitle";
import { CASE_TABS } from "./constants";

/** A case the backend refused, remembered across an in-flight retry. */
type CaseRefusal = { caseNumber: string; status?: number };

export const useCaseDetailTabs = () => {
  const { id: caseNumber, templateId } = useParams({ strict: false }) as {
    id: string;
    templateId?: string;
  };
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const {
    handleNavigationWithWarning,
    showWarningModal,
    isRecordingWarning,
    recordingExamineeName,
    handleConfirmNavigation,
    handleCancelNavigation,
  } = useNavigationWarning();

  const {
    data: caseData,
    isLoading: isCaseLoading,
    isFetching: isCaseFetching,
    error: caseError,
    refetch: refetchCase,
  } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  // MDRS-343: which screen the shell owes the viewer. Two React Query lifecycle
  // details make the naive `caseError ? classify : OK` wrong, and both are load
  // bearing — see use-case-detail-tabs.test.ts:
  //
  // 1. A FAILED REFETCH KEEPS `data`. query-core's error reducer spreads the
  //    previous state and only flags it invalidated, so `data` and `error` coexist.
  //    Classifying on `error` alone would let one transient background failure
  //    (any of the broad `[CLIENT_KEYS.CASES]` invalidations, or `refetchOnReconnect`
  //    once `staleTime` lapses) tear the tab subtree down mid-assessment — killing a
  //    live Scribe recording and unsaved Report Builder state, the MDRS-233 failure
  //    all over again — and it would render the examinee/claim breadcrumb directly
  //    above "You don't have access to this case". A case we already hold is
  //    readable: keep the shell and let the stale data stand.
  //
  // 2. A RETRY CLEARS `error`. `fetchState()` resets `{ error: null, status:
  //    "pending" }` whenever `data === undefined`, so clicking "Try again" would
  //    flip the state back to OK, unmount the very notice that was clicked, mount
  //    the tabs for a case that is still refused, then flip back on failure. So the
  //    refusal has to be remembered across the in-flight retry — query-core won't
  //    keep it — which is what lets the notice stay put and show its spinner.
  //
  // Remembered in state rather than a ref: a render-phase ref write isn't safe
  // under StrictMode replay (the React Compiler lint rule flags it). This is
  // React's documented "adjust state when props change" pattern, and `refusal` —
  // not `lastRefusal` — feeds the output, so correctness never waits on the state
  // update landing.
  const [lastRefusal, setLastRefusal] = useState<CaseRefusal | null>(null);

  const refusal: CaseRefusal | null = caseData
    ? // A case we already hold is readable — see (1) above.
      null
    : caseError
      ? { caseNumber, status: (caseError as ApiError).status }
      : // No data, no error: either a retry in flight (keep the refusal) or a
        // different/fresh case (drop it).
        lastRefusal?.caseNumber === caseNumber
        ? lastRefusal
        : null;

  if (
    refusal?.caseNumber !== lastRefusal?.caseNumber ||
    refusal?.status !== lastRefusal?.status
  ) {
    setLastRefusal(refusal);
  }

  const accessState = refusal
    ? caseAccessStateForStatus(refusal.status)
    : CASE_ACCESS_STATE.OK;

  // MDRS-310: service type is admin-only on the case detail. Hiding it from the
  // Case Details card while leaving it in this header line would defeat that, so
  // the segment is gated here too.
  const isAdmin = useIsAdmin();

  const activeTab =
    CASE_TABS.find((tab) => pathname.includes(`/${tab.id}`))?.id ?? "";

  const isOnReportPreview = pathname.endsWith("/preview");

  const handleTabChange = (tabId: string) => {
    navigate({ to: `${NAVIGATION_PATHS.REPORT_STUDIO}/${caseNumber}/${tabId}` });
  };

  // MDRS-343: only describe a case that actually loaded. These interpolated
  // undefineds used to render as the literal "Examinee Name:  - Claim: " on every
  // refused fetch, which is most of what made a 403 look like a broken page.
  const caseTitle = caseData
    ? `Examinee Name: ${caseData.examinee_name ?? ""} - Claim: ${caseData.claim_number ?? ""}`
    : "";

  // Bulleted subtitle line rendered under the breadcrumb, e.g.
  // "Examinee: Samantha Jones • Claim: 123456789 • Workers Compensation".
  const caseSubtitle = caseData
    ? buildCaseSubtitle({
        examineeName: caseData.examinee_name,
        claimNumber: caseData.claim_number,
        serviceType: isAdmin ? caseData.service_type : null,
      })
    : "";

  const isOnOverview = activeTab === "";

  // A live recording must never cross a case boundary silently — guard every
  // href-bearing crumb uniformly rather than special-casing "Report Studio":
  // handleNavigationWithWarning already no-ops for same-case targets, so this
  // costs nothing for the case-title/tab crumbs while covering the crumb that
  // actually leaves the case.
  const guardedClick = (href: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    handleNavigationWithWarning(() => navigate({ to: href }), href);
  };

  const breadcrumbs: Array<{
    label: string;
    href?: string;
    onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  }> = [
    {
      label: "Report Studio",
      href: NAVIGATION_PATHS.REPORT_STUDIO,
      onClick: guardedClick(NAVIGATION_PATHS.REPORT_STUDIO),
    },
  ];

  if (caseData) {
    const caseTitleHref = isOnOverview
      ? undefined
      : `${NAVIGATION_PATHS.REPORT_STUDIO}/${caseNumber}`;
    breadcrumbs.push({
      label: caseTitle,
      href: caseTitleHref,
      onClick: caseTitleHref ? guardedClick(caseTitleHref) : undefined,
    });
  }

  // With no case there is no tab crumb to add either: the shell renders the
  // access notice instead of the tab bar, so a crumb for a tab the viewer can't
  // reach would be a dead label pointing at nothing.
  if (caseData && activeTab) {
    const activeTabLabel = CASE_TABS.find((tab) => tab.id === activeTab)?.label;
    if (activeTabLabel) {
      const activeTabHref =
        isOnReportPreview && activeTab === "report-builder" && templateId
          ? `${NAVIGATION_PATHS.REPORT_STUDIO}/${caseNumber}/report-builder/template/${templateId}`
          : undefined;
      breadcrumbs.push({
        label: activeTabLabel,
        href: activeTabHref,
        onClick: activeTabHref ? guardedClick(activeTabHref) : undefined,
      });
    }
    if (isOnReportPreview) {
      breadcrumbs.push({ label: "Report Preview" });
    }
  } else if (caseData) {
    breadcrumbs.push({ label: "Case Detail" });
  } else if (accessState !== CASE_ACCESS_STATE.OK) {
    // Name the state as the terminal crumb. Without it "Report Studio" is the
    // last item, and `Breadcrumbs` renders the last item as a disabled
    // `aria-current="page"` span — a dead label on the one screen whose whole job
    // is getting the viewer back out.
    breadcrumbs.push({ label: CASE_ACCESS_COPY[accessState].crumb });
  }

  return {
    accessState,
    activeTab,
    breadcrumbs,
    caseTitle,
    caseSubtitle,
    isCaseLoading,
    isCaseFetching,
    isOnOverview,
    isOnReportPreview,
    handleTabChange,
    refetchCase,
    showWarningModal,
    isRecordingWarning,
    recordingExamineeName,
    handleConfirmNavigation,
    handleCancelNavigation,
  };
};
